import { useEffect, useRef, useState } from 'react';
import { Store, Loader2, LogIn, KeyRound, BarChart3, Send, Clock, CheckCircle2, XCircle, RefreshCw, LogOut, Eye, MousePointerClick, Gift, QrCode, Copy, Check, Share2, Download, Upload, ShieldCheck, Ticket, Lock, Bell } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView, trackEvent, flushEvents } from '../lib/analytics';
import { appendReferralCode } from '../lib/utils';

interface MerchantInfo {
  id: number;
  name: string;
  contactName?: string | null;
  phone: string;
  agreedAt?: number | null;
  agreementVersion?: string | null;
}

interface MerchantOffer {
  id: number;
  key: string;
  region: string;
  restaurantName: string;
  title: string;
  imageUrl?: string | null;
  validUntil?: number | null;
  maxClaims?: number | null;
  isActive: number;
  hasVerifyCode: number;
  allowReclaim: number;
  stats: {
    impressions: number;
    clicks: number;
    redeems: number;
    claimsActive: number;
    claimsRedeemed: number;
  };
}

interface MerchantSubmission {
  id: number;
  restaurantName: string;
  title: string;
  status: 'pending' | 'approved' | 'rejected';
  adminNotes?: string | null;
  createdAt: number;
}

const TOKEN_KEY = 'merchant_center_token';

export default function MerchantCenter() {
  // QR poster background — dedicated artwork (was CMS home.hero.image)
  const POSTER_BG = '/media/media/1786866403_poster.webp';
  const [stage, setStage] = useState<'checking' | 'login' | 'otp' | 'agreement' | 'portal'>('checking');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [merchant, setMerchant] = useState<MerchantInfo | null>(null);
  const [offers, setOffers] = useState<MerchantOffer[]>([]);
  const [submissions, setSubmissions] = useState<MerchantSubmission[]>([]);

  // Submit-offer form (full parity with /merchant/submit-offer)
  const [fTitle, setFTitle] = useState('');
  const [fDesc, setFDesc] = useState('');
  const [fRegion, setFRegion] = useState('local');
  const [fRestaurantName, setFRestaurantName] = useState('');
  const [fMaxClaims, setFMaxClaims] = useState('');
  const [fValidUntil, setFValidUntil] = useState('');
  const [fVerifyCode, setFVerifyCode] = useState('');
  const [fInstructions, setFInstructions] = useState('');
  const [fImageUrl, setFImageUrl] = useState('');
  const [fUploading, setFUploading] = useState(false);
  const [formMsg, setFormMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Portal tabs + insights teaser (aggregate numbers; customer list stays locked server-side)
  const [portalTab, setPortalTab] = useState<'overview' | 'offers' | 'promo' | 'insights'>('overview');
  const [offersSubTab, setOffersSubTab] = useState<'performance' | 'submit'>('performance');
  const [insights, setInsights] = useState<{ consentedCount: number; uniqueClaimants: number; redeemedTotal: number }>({ consentedCount: 0, uniqueClaimants: 0, redeemedTotal: 0 });

  useEffect(() => {
    if (merchant?.name && !fRestaurantName) setFRestaurantName(merchant.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant]);

  const applyDescTemplate = (content: string) => {
    setFDesc((cur) => (cur.trim() ? `${cur.trim()}\n\n${content}` : content));
  };

  const applyInstructionTemplate = (content: string) => {
    setFInstructions((cur) => (cur.trim() ? `${cur.trim()}\n\n${content}` : content));
  };

  const handleFormImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setFUploading(true);
    setFormMsg('');
    try {
      const { smartCompressImage } = await import('../lib/imageCompression');
      // 商家中心提交嘅係優惠圖片（會經 WhatsApp 發送 coupon），必須 JPEG
      const compressed = await smartCompressImage(file, { format: 'jpeg' }).catch(() => null);
      const uploadFile = compressed?.file ?? file;
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await fetch('/api/public/media/upload', { method: 'POST', body: fd });
      const d = await res.json().catch(() => ({}));
      const data = d.data || d;
      if (!res.ok || !data?.url) throw new Error(data?.error || '上傳失敗');
      setFImageUrl(data.url);
    } catch (err) {
      setFormMsg(err instanceof Error ? err.message : '圖片上傳失敗');
    } finally {
      setFUploading(false);
    }
  };

  // Verify code editor per offer
  const [codeTarget, setCodeTarget] = useState<MerchantOffer | null>(null);
  const [codeValue, setCodeValue] = useState('');

  // Share toolkit
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const qrRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // 轉介歸因鏈：商家若同時是辣谷之友，其分享的優惠連結／QR 海報會帶上
  // 其轉介碼（無碼則原樣），與全站「分享必繼承轉介」規則一致。
  const shortUrl = (o: MerchantOffer) => appendReferralCode(`${window.location.origin}/c/${o.key}`);

  const trackShare = (o: MerchantOffer, method: string) => {
    trackEvent('offer_share', { offerKey: o.key, method, via: 'merchant_center' });
    flushEvents();
  };

  const copyShort = async (o: MerchantOffer) => {
    const text = shortUrl(o);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedKey(o.key);
    setTimeout(() => setCopiedKey(null), 2000);
    trackShare(o, 'copy');
  };

  const waShare = (o: MerchantOffer) => {
    const msg = `🎁【${o.restaurantName || merchant?.name || '香港辣谷'} × 香港辣谷】${o.title}\n即刻免費領取 👉 ${shortUrl(o)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
    trackShare(o, 'whatsapp');
  };

  // Compose a branded table-tent PNG: hero background + frosted card + QR + CTA
  const downloadQrPoster = async (o: MerchantOffer) => {
    const svg = qrRefs.current[o.id]?.querySelector('svg');
    if (!svg) return;

    const loadImg = (src: string) =>
      new Promise<HTMLImageElement | null>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });

    const serializer = new XMLSerializer();
    const svgBlob = new Blob([serializer.serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const [qrImg, bgImg, logoImg] = await Promise.all([
      loadImg(svgUrl),
      loadImg(POSTER_BG),
      loadImg('/logo.png'),
    ]);
    if (!qrImg) {
      URL.revokeObjectURL(svgUrl);
      return;
    }

    const W = 640;
    const H = 960;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background: hero image cover-fit
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, W, H);
    if (bgImg) {
      const scale = Math.max(W / bgImg.width, H / bgImg.height);
      const dw = bgImg.width * scale;
      const dh = bgImg.height * scale;
      ctx.drawImage(bgImg, (W - dw) / 2, (H - dh) / 2, dw, dh);
    }
    // Light overlay — the approved light background (hero imagery softened)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.fillRect(0, 0, W, H);

    const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    };

    // Frosted card — 50% translucent so the brand imagery shows through
    const cardX = 56;
    const cardY = 140;
    const cardW = W - 112;
    const cardH = 700;
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 6;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    roundRect(cardX, cardY, cardW, cardH, 28);
    ctx.fill();
    ctx.restore();

    ctx.textAlign = 'center';
    // Restaurant name — brand red
    ctx.fillStyle = '#d9381e';
    ctx.font = 'bold 40px "Noto Sans TC", "Microsoft JhengHei", sans-serif';
    ctx.fillText(o.restaurantName || merchant?.name || '', W / 2, cardY + 84);
    // Red divider
    ctx.fillRect(W / 2 - 30, cardY + 108, 60, 4);
    // Offer title
    ctx.fillStyle = '#1c1917';
    ctx.font = 'bold 26px "Noto Sans TC", "Microsoft JhengHei", sans-serif';
    ctx.fillText(o.title, W / 2, cardY + 156);
    // Solid white plate under the QR — sticker look, guarantees scan contrast
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 3;
    ctx.fillStyle = '#ffffff';
    roundRect(W / 2 - 196, cardY + 200, 392, 392, 16);
    ctx.fill();
    ctx.restore();
    ctx.strokeStyle = '#e7e5e4';
    ctx.lineWidth = 2;
    roundRect(W / 2 - 196, cardY + 200, 392, 392, 16);
    ctx.stroke();
    // QR
    ctx.drawImage(qrImg, W / 2 - 180, cardY + 216, 360, 360);
    // CTA button
    ctx.fillStyle = '#d9381e';
    roundRect(W / 2 - 200, cardY + 612, 400, 56, 28);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px "Noto Sans TC", "Microsoft JhengHei", sans-serif';
    ctx.fillText('掃碼免費領取優惠', W / 2, cardY + 650);

    // Brand footer — pill-style link badge, centered at the bottom
    const footerText = '香港🌶️辣谷 HKSpicyValley.com';
    ctx.font = 'bold 23px "Noto Sans TC", "Microsoft JhengHei", sans-serif';
    const tw = ctx.measureText(footerText).width;
    const pillW = tw + 56;
    const pillH = 52;
    const pillX = (W - pillW) / 2;
    const pillY = 886;
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 3;
    roundRect(pillX, pillY, pillW, pillH, pillH / 2);
    ctx.fillStyle = '#1c1917';
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.fillText(footerText, W / 2, pillY + 34);

    URL.revokeObjectURL(svgUrl);
    trackShare(o, 'qr');

    canvas.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `qr-${o.key}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    }, 'image/png');
  };

  useEffect(() => {
    trackPageView('/merchant/center');
    const magic = new URLSearchParams(window.location.search).get('t');
    if (magic) {
      // Standing magic link — exchange for a session, then clean the URL
      fetch('/api/merchant/auth/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: magic }),
      })
        .then((r) => r.json())
        .then((d) => {
          const data = d.data || d;
          if (!data || data.error) throw new Error(data?.error || '連結無效');
          localStorage.setItem(TOKEN_KEY, data.token);
          window.history.replaceState(null, '', '/merchant/center');
          return loadPortal(data.token);
        })
        .catch((e) => {
          setError(e.message || '連結無效');
          setStage('login');
        });
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setStage('login'); return; }
    loadPortal(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPortal = async (token: string) => {
    try {
      const res = await fetch('/api/merchant/me', { headers: { 'x-merchant-token': token } });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) {
        localStorage.removeItem(TOKEN_KEY);
        setError(data?.error || '');
        setStage('login');
        return;
      }
      setMerchant(data.merchant);
      setOffers(Array.isArray(data.offers) ? data.offers : []);
      setSubmissions(Array.isArray(data.submissions) ? data.submissions : []);
      if (data.insights) setInsights(data.insights);
      // First-login gate: explicit agreement to the service terms (recorded as evidence)
      setStage(data.merchant?.agreedAt ? 'portal' : 'agreement');
      setError('');
    } catch {
      setStage('login');
    }
  };

  const requestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(''); setInfo('');
    try {
      const res = await fetch('/api/merchant/auth/request-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '發送失敗');
      setStage('otp');
      setInfo(data.throttled ? '發送太頻繁，請 1 分鐘後再試' : '驗證碼已經 WhatsApp 發送俾你（10 分鐘內有效）');
    } catch (err) {
      setError(err instanceof Error ? err.message : '發送失敗');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/merchant/auth/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code: otp }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '驗證失敗');
      localStorage.setItem(TOKEN_KEY, data.token);
      await loadPortal(data.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : '驗證失敗');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      fetch('/api/merchant/auth/logout', { method: 'POST', headers: { 'x-merchant-token': token } }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    setStage('login'); setMerchant(null); setOtp('');
  };

  const submitOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormMsg(''); setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const res = await fetch('/api/merchant/offers', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-merchant-token': token },
        body: JSON.stringify({
          region: fRegion,
          restaurantName: fRestaurantName.trim(),
          title: fTitle.trim(),
          description: fDesc.trim(),
          imageUrl: fImageUrl,
          redeemInstructions: fInstructions.trim(),
          validUntil: fValidUntil ? Math.floor(new Date(`${fValidUntil}T23:59:59`).getTime() / 1000) : 0,
          maxClaims: parseInt(fMaxClaims, 10) || 0,
          merchantVerifyCode: fVerifyCode.trim(),
        }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '提交失敗');
      setFormMsg('✅ 已提交審批，香港辣谷會盡快處理');
      setFTitle(''); setFDesc(''); setFMaxClaims(''); setFInstructions('');
      setFValidUntil(''); setFVerifyCode(''); setFImageUrl('');
      loadPortal(token);
    } catch (err) {
      setFormMsg(err instanceof Error ? err.message : '提交失敗');
    } finally {
      setLoading(false);
    }
  };

  const saveVerifyCode = async () => {
    if (!codeTarget || !/^\d{4}$/.test(codeValue)) return;
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const res = await fetch(`/api/merchant/offers/${codeTarget.id}/verify-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-merchant-token': token },
        body: JSON.stringify({ code: codeValue }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '更新失敗');
      setCodeTarget(null); setCodeValue('');
      loadPortal(token);
    } catch (err) {
      setFormMsg(err instanceof Error ? err.message : '更新失敗');
    } finally {
      setLoading(false);
    }
  };

  const takeOffline = async (o: MerchantOffer) => {
    if (!confirm(`確定下架「${o.title}」？下架後顧客將無法領取；如需重新上架，請 WhatsApp 聯絡我們。`)) return;
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const res = await fetch(`/api/merchant/offers/${o.id}/take-offline`, {
        method: 'POST',
        headers: { 'x-merchant-token': token },
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '下架失敗');
      loadPortal(token);
    } catch (err) {
      setFormMsg(err instanceof Error ? err.message : '下架失敗');
    } finally {
      setLoading(false);
    }
  };

  const acceptAgreement = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const res = await fetch('/api/merchant/agree', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-merchant-token': token },
        body: JSON.stringify({ version: '1.0' }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '確認失敗');
      setMerchant((m) => (m ? { ...m, agreedAt: data.agreedAt, agreementVersion: data.agreementVersion } : m));
      setStage('portal');
    } catch (err) {
      setError(err instanceof Error ? err.message : '確認失敗');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none';

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      <section className="relative bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 flex items-center gap-3">
            <Store size={32} /> 商家中心
          </h1>
          <p className="text-white/80">登入查看你餐廳優惠嘅實時成效 · 提交新優惠 · 自助更新核銷驗證碼</p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4">
          {stage === 'checking' && <div className="text-center py-20 text-stone-500">載入中...</div>}

          {/* ---- Login ---- */}
          {stage === 'login' && (
            <div className="max-w-md mx-auto bg-white rounded-2xl border border-stone-200 p-8">
              <LogIn className="mx-auto text-x1493-primary mb-4" size={36} />
              <h2 className="text-xl font-bold text-stone-900 text-center mb-2">商家登入</h2>
              <p className="text-sm text-stone-500 text-center mb-6">輸入登記電話，我哋會經 WhatsApp 發送驗證碼</p>
              <form onSubmit={requestOtp} className="space-y-4">
                <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="登記電話（例如 6xxxxxxx）"
                       inputMode="tel" className={inputCls} />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button type="submit" disabled={loading}
                        className="w-full bg-x1493-primary text-white font-bold py-3 rounded-xl hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />} 發送驗證碼
                </button>
              </form>
              <p className="text-xs text-stone-400 text-center mt-4">未登記成為合作商家？WhatsApp 我哋了解一下！</p>
            </div>
          )}

          {/* ---- OTP ---- */}
          {stage === 'otp' && (
            <div className="max-w-md mx-auto bg-white rounded-2xl border border-stone-200 p-8">
              <KeyRound className="mx-auto text-x1493-primary mb-4" size={36} />
              <h2 className="text-xl font-bold text-stone-900 text-center mb-2">輸入驗證碼</h2>
              <p className="text-sm text-stone-500 text-center mb-6">{info || `驗證碼已發送至 ${phone}`}</p>
              <form onSubmit={verifyOtp} className="space-y-4">
                <input required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       placeholder="6 位數字驗證碼" inputMode="numeric" className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold`} />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button type="submit" disabled={loading || otp.length !== 6}
                        className="w-full bg-x1493-primary text-white font-bold py-3 rounded-xl hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />} 登入
                </button>
                <button type="button" onClick={() => { setStage('login'); setOtp(''); setError(''); }}
                        className="w-full text-sm text-stone-500 hover:text-stone-700">← 返回重填電話</button>
              </form>
            </div>
          )}

          {/* ---- Portal ---- */}
          {/* ---- First-login agreement gate ---- */}
          {stage === 'agreement' && merchant && (
            <div className="max-w-lg mx-auto bg-white rounded-2xl border border-stone-200 p-8">
              <ShieldCheck className="mx-auto text-x1493-primary mb-4" size={36} />
              <h2 className="text-xl font-bold text-stone-900 text-center mb-2">歡迎使用商家中心</h2>
              <p className="text-sm text-stone-500 text-center mb-6">首次使用前，請確認你同意《商家服務協議》的要點：</p>
              <ul className="text-sm text-stone-700 space-y-2.5 mb-6 list-disc pl-5">
                <li>商家須保證優惠內容真實、準確、可兌現，並承擔兌換責任</li>
                <li>顧客聯絡資料僅可用於<strong>核銷該優惠</strong>；未經顧客明示同意不得用於任何推廣，不得轉售或轉移予第三方</li>
                <li>須尊重顧客拒收權；優惠有效期結束後 90 日內刪除顧客聯絡資料</li>
                <li>須遵守香港《個人資料（私隱）條例》及適用的數據保護法律</li>
                <li>平台有權審批、拒絕或下架違規優惠</li>
              </ul>
              <a href="/merchant/agreement" target="_blank" rel="noopener noreferrer"
                 className="block text-center text-xs text-x1493-primary hover:underline mb-6">閱讀完整協議 →</a>
              {error && <p className="text-sm text-red-600 text-center mb-3">{error}</p>}
              <button onClick={acceptAgreement} disabled={loading}
                      className="w-full bg-x1493-primary text-white font-bold py-3 rounded-xl hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} 我同意並進入商家中心
              </button>
              <p className="text-[10px] text-stone-400 text-center mt-3">你的同意將以時間及協議版本記錄（v1.0）</p>
            </div>
          )}

          {/* ---- Portal ---- */}
          {stage === 'portal' && merchant && (
            <div className="space-y-8">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-stone-900">{merchant.name}</h2>
                  <p className="text-sm text-stone-500">{merchant.contactName || '商家'} · {merchant.phone}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => loadPortal(localStorage.getItem(TOKEN_KEY) || '')}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-stone-300 rounded-lg text-stone-600 hover:border-x1493-primary hover:text-x1493-primary transition">
                    <RefreshCw size={14} /> 更新
                  </button>
                  <button onClick={logout}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-stone-300 rounded-lg text-stone-600 hover:border-red-400 hover:text-red-600 transition">
                    <LogOut size={14} /> 登出
                  </button>
                </div>
              </div>

              {/* Portal tabs */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { key: 'overview', label: '總覽', icon: BarChart3 },
                  { key: 'offers', label: `優惠管理${offers.length ? ` (${offers.length})` : ''}`, icon: Ticket },
                  { key: 'promo', label: '推廣工具', icon: Share2 },
                  { key: 'insights', label: '客源洞察', icon: Lock },
                ].map((t) => {
                  const TIcon = t.icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setPortalTab(t.key as typeof portalTab)}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition ${
                        portalTab === t.key
                          ? 'bg-x1493-primary text-white shadow'
                          : 'bg-white text-stone-600 border border-stone-200 hover:border-x1493-primary'
                      }`}
                    >
                      <TIcon size={14} /> {t.label}
                    </button>
                  );
                })}
              </div>

              {/* ---- Overview tab ---- */}
              {portalTab === 'overview' && (() => {
                const totals = offers.reduce(
                  (acc, o) => ({
                    impressions: acc.impressions + o.stats.impressions,
                    clicks: acc.clicks + o.stats.clicks,
                    claims: acc.claims + o.stats.claimsActive + o.stats.claimsRedeemed,
                    redeemed: acc.redeemed + o.stats.claimsRedeemed,
                  }),
                  { impressions: 0, clicks: 0, claims: 0, redeemed: 0 }
                );
                const redeemRate = totals.claims > 0 ? Math.round((totals.redeemed / totals.claims) * 100) : 0;
                const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100).toFixed(1) : '0.0';
                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: '總曝光', value: totals.impressions.toLocaleString(), icon: Eye },
                        { label: '領取', value: totals.claims.toLocaleString(), icon: Gift },
                        { label: '已兌換（到店）', value: totals.redeemed.toLocaleString(), icon: QrCode },
                        { label: '已同意通知顧客', value: insights.consentedCount.toLocaleString(), icon: Bell },
                      ].map((k) => {
                        const KIcon = k.icon;
                        return (
                          <div key={k.label} className="bg-white rounded-2xl border border-stone-200 p-4 text-center">
                            <KIcon size={18} className="mx-auto text-x1493-primary mb-1.5" />
                            <p className="text-2xl font-bold text-stone-900">{k.value}</p>
                            <p className="text-[11px] text-stone-400 mt-0.5">{k.label}</p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="bg-white rounded-2xl border border-stone-200 p-5">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-bold text-stone-700">整體健康度</span>
                        <span className="text-stone-400 text-xs">曝光→點擊 {ctr}% · 領取→到店 {redeemRate}%</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden flex">
                        <div className="h-full bg-x1493-primary" style={{ width: `${Math.min(100, totals.claims ? (totals.redeemed / totals.claims) * 100 : 0)}%` }} />
                      </div>
                      <p className="text-xs text-stone-400 mt-3">
                        想提升到店率？去<a onClick={() => setPortalTab('promo')} className="text-x1493-primary font-bold cursor-pointer hover:underline">推廣工具</a>
                        下載 QR 海報擺店內，或用 WhatsApp 分享優惠俾你的客人。
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Offers with funnel */}
              {portalTab === 'offers' && (
                <>
              {/* Secondary tabs inside 優惠管理 */}
              <div className="flex gap-2 border-b border-stone-200 pb-3">
                {[
                  { key: 'performance', label: `我的優惠成效${offers.length ? ` (${offers.length})` : ''}` },
                  { key: 'submit', label: '提交新優惠' },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setOffersSubTab(t.key as typeof offersSubTab)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                      offersSubTab === t.key
                        ? 'bg-stone-900 text-white'
                        : 'text-stone-500 hover:text-stone-800'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {offersSubTab === 'performance' && (
              <div>
                <h3 className="font-bold text-lg text-stone-900 mb-3 flex items-center gap-2">
                  <BarChart3 size={18} className="text-x1493-primary" /> 我的優惠成效
                </h3>
                {offers.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-stone-500">
                    暫時未有掛在你名下嘅優惠。提交新優惠後，我哋審批上架就會顯示在這裡。
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {offers.map((o) => {
                      const s = o.stats;
                      const total = s.claimsActive + s.claimsRedeemed;
                      const redeemRate = total > 0 ? Math.round((s.claimsRedeemed / total) * 100) : 0;
                      return (
                        <div key={o.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                          <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                            {o.imageUrl ? <img src={o.imageUrl} alt={o.title} className="w-full h-full object-cover" /> :
                              <div className="w-full h-full flex items-center justify-center text-stone-300"><Store size={32} /></div>}
                            <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${o.isActive ? 'bg-green-500 text-white' : 'bg-stone-400 text-white'}`}>
                              {o.isActive ? '上架中' : '已下架'}
                            </span>
                          </div>
                          <div className="p-4">
                            <p className="font-bold text-stone-900">{o.title}</p>
                            <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                              <div><Eye size={14} className="mx-auto text-stone-400 mb-1" /><p className="font-bold text-stone-800">{s.impressions}</p><p className="text-[10px] text-stone-400">曝光</p></div>
                              <div><MousePointerClick size={14} className="mx-auto text-stone-400 mb-1" /><p className="font-bold text-stone-800">{s.clicks}</p><p className="text-[10px] text-stone-400">點擊</p></div>
                              <div><Gift size={14} className="mx-auto text-stone-400 mb-1" /><p className="font-bold text-stone-800">{total}</p><p className="text-[10px] text-stone-400">領取</p></div>
                              <div><QrCode size={14} className="mx-auto text-stone-400 mb-1" /><p className="font-bold text-stone-800">{s.claimsRedeemed}</p><p className="text-[10px] text-stone-400">已兌換</p></div>
                            </div>
                            {total > 0 && (
                              <div className="mt-3">
                                <div className="flex justify-between text-[10px] text-stone-400 mb-1"><span>兌換率</span><span>{redeemRate}%</span></div>
                                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-x1493-primary rounded-full" style={{ width: `${redeemRate}%` }} />
                                </div>
                              </div>
                            )}
                            {/* Promo toolkit lives in its own tab now */}
                            <button onClick={() => setPortalTab('promo')}
                                    className="mt-4 pt-4 border-t border-stone-100 w-full flex items-center justify-between group">
                              <span className="text-xs font-bold text-stone-500 flex items-center gap-1">
                                <Share2 size={12} className="text-x1493-primary" /> 分享優惠俾你嘅客人
                              </span>
                              <span className="text-xs text-x1493-primary font-bold group-hover:underline">推廣工具 →</span>
                            </button>

                            <div className="flex items-center justify-between mt-4 gap-2">
                              <a href={`/c/${o.key}`} target="_blank" rel="noopener noreferrer"
                                 className="text-xs text-x1493-primary font-bold hover:underline shrink-0">查看優惠頁 →</a>
                              <div className="flex items-center gap-1.5">
                                {o.isActive ? (
                                  <button onClick={() => takeOffline(o)}
                                          className="text-xs px-2.5 py-1 rounded-lg bg-stone-100 text-stone-500 hover:bg-red-50 hover:text-red-600 font-medium transition">
                                    下架此優惠
                                  </button>
                                ) : (
                                  <span className="text-[10px] text-stone-400">已下架 · 重新上架請聯絡我們</span>
                                )}
                                <button onClick={() => { setCodeTarget(o); setCodeValue(''); }}
                                        className="text-xs px-2.5 py-1 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 font-medium">
                                  核銷驗證碼：{o.hasVerifyCode ? '已設定 ✓' : '未設定'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              )}

              {offersSubTab === 'submit' && (
                <>
              {/* Submit new offer */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
                  <Send size={18} className="text-x1493-primary" /> 提交新優惠
                </h3>
                <p className="text-xs text-stone-500 mt-1">填妥後經香港辣谷審批上架，批准後自動歸入你的帳戶。</p>
                  <form onSubmit={submitOffer} className="mt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-stone-500 mb-1">餐廳名稱 *</label>
                        <input required value={fRestaurantName} onChange={(e) => setFRestaurantName(e.target.value)} className={inputCls} />
                      </div>
                      <select value={fRegion} onChange={(e) => setFRegion(e.target.value)} className={inputCls + ' bg-white'}>
                        <option value="local">香港本地</option>
                        <option value="mainland">內地</option>
                      </select>
                    </div>
                    <input required value={fTitle} onChange={(e) => setFTitle(e.target.value)} placeholder="優惠標題 *（例如：全單 9 折）" className={inputCls} />
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-stone-500">優惠詳情 / 細則 *</label>
                        <span className="text-[10px] text-stone-400">點擊模板快速填入</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {[
                          { label: '香港餐廳（詳細）', content: '`店舖簡介`\n（請簡介餐廳特色、招牌菜式）\n\n`優惠內容`\n凡惠顧滿 $XXX，出示本福利圖，即可享 XXXX。\n不可與其他優惠同時使用，優惠最終解釋權歸店鋪所有。\n\n`📍店舖地址：`\n（請填寫完整地址）' },
                          { label: '香港餐廳（簡短）', content: '凡到店出示本福利圖，即可享 XXXX。\n不可與其他優惠同時使用，優惠最終解釋權歸店鋪所有。\n\n`📍店舖地址：`\n（請填寫完整地址）' },
                          { label: '內地餐廳', content: '`店舖簡介`\n（請簡介餐廳特色、招牌菜式）\n\n`優惠內容`\n凡惠顧任何一碗拉麵 / 套餐，出示本福利圖，即可免費獲贈 XXXX。\n每單限贈一份，不可與其他優惠同時使用。\n\n`店鋪地址：`\n（請填寫完整地址）' },
                        ].map((t) => (
                          <button key={t.label} type="button" onClick={() => applyDescTemplate(t.content)}
                                  className="px-2 py-0.5 text-[11px] rounded-lg border border-stone-300 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary transition">
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <textarea required rows={4} value={fDesc} onChange={(e) => setFDesc(e.target.value)} placeholder="請詳細說明優惠內容、適用條件、使用限制等" className={inputCls} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-stone-500">兌換說明（可選）</label>
                        <span className="text-[10px] text-stone-400">點擊模板快速填入</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        <button type="button" onClick={() => applyInstructionTemplate('預設：出示此訊息同兌換碼畀店員，即可享用優惠。')}
                                className="px-2 py-0.5 text-[11px] rounded-lg border border-stone-300 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary transition">
                          預設版
                        </button>
                        <button type="button" onClick={() => applyInstructionTemplate('出示此訊息同兌換碼畀店員，即可享用優惠。\n- 只適用堂食\n- 不可與店內其他折扣、套餐同時使用\n- 優惠最終解釋權歸店鋪所有')}
                                className="px-2 py-0.5 text-[11px] rounded-lg border border-stone-300 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary transition">
                          詳細版
                        </button>
                      </div>
                      <textarea rows={2} value={fInstructions} onChange={(e) => setFInstructions(e.target.value)} placeholder="預設：出示此訊息同兌換碼畀店員，即可享用優惠。" className={inputCls} />
                    </div>

                    {/* Image upload */}
                    <div>
                      <label className="block text-xs font-medium text-stone-500 mb-1.5">優惠圖片（可選，會自動壓縮）</label>
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFormImage} />
                      {!fImageUrl ? (
                        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={fUploading}
                                className="w-full h-28 border-2 border-dashed border-stone-300 rounded-xl flex flex-col items-center justify-center gap-1.5 text-stone-500 hover:border-x1493-primary hover:text-x1493-primary transition disabled:opacity-50">
                          {fUploading ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} />}
                          <span className="text-xs">{fUploading ? '上傳中...' : '點擊上傳優惠圖片'}</span>
                          <span className="text-[10px] text-stone-400">JPEG / PNG / GIF / WebP，最大 5MB</span>
                        </button>
                      ) : (
                        <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-100">
                          <img src={fImageUrl} alt="優惠圖片預覽" className="w-full h-36 object-cover" />
                          <button type="button" onClick={() => setFImageUrl('')}
                                  className="absolute top-2 right-2 px-2.5 py-1 bg-black/60 text-white text-xs rounded-lg hover:bg-black/80">
                            移除
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-stone-500 mb-1">有效期至</label>
                        <input type="date" value={fValidUntil} onChange={(e) => setFValidUntil(e.target.value)} className={inputCls} />
                        <p className="text-[10px] text-stone-400 mt-0.5">留空＝長期</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-stone-500 mb-1">名額</label>
                        <input type="number" min="1" value={fMaxClaims} onChange={(e) => setFMaxClaims(e.target.value)} placeholder="不限" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-stone-500 mb-1">核銷驗證碼</label>
                        <input value={fVerifyCode} onChange={(e) => setFVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                               placeholder="1493" inputMode="numeric" className={inputCls + ' text-center font-mono tracking-widest'} />
                        <p className="text-[10px] text-stone-400 mt-0.5">4 位數字；留空用平台預設</p>
                      </div>
                    </div>

                    {formMsg && <p className="text-sm text-stone-700 bg-stone-50 border border-stone-200 rounded-lg p-3">{formMsg}</p>}
                    <button type="submit" disabled={loading || fUploading}
                            className="bg-x1493-primary text-white font-bold px-6 py-2.5 rounded-xl hover:bg-red-700 transition flex items-center gap-2 disabled:opacity-50">
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} 提交審批
                    </button>
                  </form>
              </div>

              {/* Submission history */}
              {submissions.length > 0 && (
                <div className="bg-white rounded-2xl border border-stone-200 p-6">
                  <h3 className="font-bold text-lg text-stone-900 mb-3">我的提交記錄</h3>
                  <ul className="divide-y divide-stone-100">
                    {submissions.map((s) => (
                      <li key={s.id} className="py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-stone-800 truncate">{s.title}</p>
                          <p className="text-xs text-stone-400">{new Date(s.createdAt * 1000).toLocaleString('zh-HK')}</p>
                          {s.status === 'rejected' && s.adminNotes && <p className="text-xs text-red-500 mt-0.5">原因：{s.adminNotes}</p>}
                        </div>
                        {s.status === 'pending' && <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1"><Clock size={11} /> 待審批</span>}
                        {s.status === 'approved' && <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle2 size={11} /> 已上架</span>}
                        {s.status === 'rejected' && <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-600 flex items-center gap-1"><XCircle size={11} /> 未通過</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
                </>
              )}
                </>
              )}

              {/* ---- Promo tab ---- */}
              {portalTab === 'promo' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl border border-stone-200 p-5">
                    <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
                      <Share2 size={18} className="text-x1493-primary" /> 推廣工具
                    </h3>
                    <p className="text-xs text-stone-500 mt-1 mb-4">把優惠分享俾你自己的客人——他們領取優惠時，電話會（經同意後）成為你的客源。飛輪由你開始。</p>
                    {offers.length === 0 ? (
                      <p className="text-sm text-stone-400">未有優惠可推廣，先去「優惠管理」提交一張。</p>
                    ) : (
                      <div className="space-y-4">
                        {offers.map((o) => (
                          <div key={o.id} className="border border-stone-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-stone-100 shrink-0">
                                {o.imageUrl ? <img src={o.imageUrl} alt={o.title} className="w-full h-full object-cover" /> : <Store size={18} className="m-auto text-stone-300" />}
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-stone-900 text-sm truncate">{o.title}</p>
                                <p className="text-[11px] text-stone-400">最近 30 日：{o.stats.impressions} 曝光 · {o.stats.claimsActive + o.stats.claimsRedeemed} 領取</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <code className="flex-1 min-w-0 truncate text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-1.5">
                                {shortUrl(o).replace('https://', '')}
                              </code>
                              <button
                                onClick={() => copyShort(o)}
                                className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition ${
                                  copiedKey === o.key ? 'bg-green-600 text-white' : 'bg-x1493-primary text-white hover:bg-red-700'
                                }`}
                              >
                                {copiedKey === o.key ? <Check size={12} /> : <Copy size={12} />}
                                {copiedKey === o.key ? '已複製' : '複製'}
                              </button>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => waShare(o)}
                                      className="flex-1 py-2 rounded-lg text-xs font-bold bg-[#25D366] text-white hover:bg-[#128C7E] transition flex items-center justify-center gap-1.5">
                                <Send size={12} /> WhatsApp 分享
                              </button>
                              <button onClick={() => downloadQrPoster(o)}
                                      className="flex-1 py-2 rounded-lg text-xs font-bold bg-stone-100 text-stone-700 hover:bg-stone-200 transition flex items-center justify-center gap-1.5">
                                <Download size={12} /> 下載 QR 海報
                              </button>
                            </div>
                            {/* Hidden QR node used by the poster renderer */}
                            <div ref={(el) => { qrRefs.current[o.id] = el; }} className="hidden" aria-hidden="true">
                              <QRCodeSVG value={shortUrl(o)} size={480} level="M" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ---- Insights tab (locked paywall) ---- */}
              {portalTab === 'insights' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: '領取過優惠的顧客', value: insights.uniqueClaimants },
                      { label: '實際到店兌換', value: insights.redeemedTotal },
                      { label: '已同意收你優惠資訊', value: insights.consentedCount },
                    ].map((k) => (
                      <div key={k.label} className="bg-white rounded-2xl border border-stone-200 p-4 text-center">
                        <p className="text-2xl font-bold text-stone-900">{k.value.toLocaleString()}</p>
                        <p className="text-[11px] text-stone-400 mt-0.5">{k.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden relative">
                    <div className="p-5 blur-[3px] select-none pointer-events-none" aria-hidden="true">
                      <p className="text-xs font-bold text-stone-500 mb-2">顧客名單（示意）</p>
                      {['9123 xxxx · 千歲 · 已到店 2 次', '6345 xxxx · 醉燒亭 · 已同意通知', '5531 xxxx · 新客 · 未開通知'].map((row) => (
                        <div key={row} className="py-2 border-b border-stone-100 text-sm text-stone-600">{row}</div>
                      ))}
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] p-6 text-center">
                      <Lock className="text-stone-400 mb-2" size={28} />
                      <p className="font-bold text-stone-800">客源名單 · 付費方案解鎖</p>
                      <p className="text-xs text-stone-500 mt-1 max-w-xs">解鎖後可查看已同意接收你優惠資訊的顧客名單及到店記錄導出（只含已同意顧客，符合 PDPO）</p>
                      <a href={`https://wa.me/85260771493?text=${encodeURIComponent('我想了解商家中心「客源洞察」付費方案')}`}
                         target="_blank" rel="noopener noreferrer"
                         className="mt-4 inline-flex items-center gap-2 bg-x1493-primary text-white font-bold px-6 py-2.5 rounded-xl hover:bg-red-700 transition">
                        <Send size={15} /> WhatsApp 查詢升級
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Verify-code modal */}
      {codeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setCodeTarget(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-stone-900 mb-1">核銷驗證碼</h2>
            <p className="text-xs text-stone-500 mb-4">{codeTarget.title} — 店員核銷時輸入嘅 4 位數字碼</p>
            <input value={codeValue} onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                   placeholder="4 位數字" inputMode="numeric"
                   className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-center text-2xl tracking-[0.5em] font-bold focus:ring-2 focus:ring-x1493-primary outline-none" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setCodeTarget(null)} className="flex-1 px-4 py-2 bg-stone-100 text-stone-600 rounded-lg text-sm font-medium">取消</button>
              <button onClick={saveVerifyCode} disabled={codeValue.length !== 4 || loading}
                      className="flex-1 px-4 py-2 bg-x1493-primary text-white rounded-lg text-sm font-bold disabled:opacity-50">儲存</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
